/**
 * `<UserName>` — the ONLY way an audit user id reaches the screen (task 999).
 *
 * Report: `project/reports/innovate-app-user-attribution-names-not-ids.md`.
 *
 * "Created by", "Published by", "Deployed by", "Deleted by" all hold an AMS
 * user UUID. A UUID is unreadable, so no component renders one of those fields
 * directly — it renders this, which resolves the id against the account's
 * member list (`useUserNames`) and shows the name.
 *
 * Three things it guarantees the seven call sites do not have to repeat:
 *   1. While the member list is loading a short skeleton renders, so the UUID
 *      never flashes before the name replaces it.
 *   2. The id stays available on hover as the `title`, so a support
 *      conversation can still reference the exact value on screen.
 *   3. The system user id and ids no longer in the account get their own
 *      labels ("Innovate", "Former member") rather than a bare UUID.
 */

import { useUserNames } from '../../hooks/useUserNames';

interface UserNameProps {
  /** AMS user id from an audit field (`createdBy`, `publishedBy`, …). */
  userId: string;
  /** Optional class applied to the rendered name. */
  className?: string;
}

export function UserName({ userId, className }: UserNameProps) {
  const { resolve } = useUserNames();
  const resolved = resolve(userId);

  if (!resolved) {
    return (
      <span
        data-testid="user-name-loading"
        aria-hidden="true"
        className="inline-block w-20 h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse align-middle"
      />
    );
  }

  return (
    <span data-testid="user-name" data-kind={resolved.kind} title={userId} className={className}>
      {resolved.name}
    </span>
  );
}

export default UserName;
